import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiUpload, FiFile, FiTrash2, FiDownload } from 'react-icons/fi';

import firebase from '@/config/firebase.config';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { addNotification, notificationStates } from '@/components/Notification';
import styles from './AttachmentsSection.module.scss';

const database = firebase.firestore();

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

export const AttachmentsSection = ({ taskId, currentUser }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const attachmentsRef = useCallback(
    () =>
      database.collection('taskDatabase').doc(taskId).collection('attachments'),
    [taskId]
  );

  const fetchAttachments = useCallback(async () => {
    if (!taskId) return;
    const snapshot = await attachmentsRef().orderBy('uploadedAt', 'desc').get();
    setAttachments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  }, [taskId, attachmentsRef]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const uploadOneFile = async (file) => {
    const storagePath = `task-attachments/${taskId}/${Math.floor(
      Math.random() * 1e5
    )}-${file.name}`;
    const storageRef = firebase.storage().ref().child(storagePath);
    const snapshot = await storageRef.put(file, { contentType: file.type });
    const url = await snapshot.ref.getDownloadURL();
    await attachmentsRef().add({
      name: file.name,
      size: file.size,
      type: file.type,
      url,
      storagePath,
      uploadedBy: currentUser?.id || null,
      uploadedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || !taskId) return;

    setUploading(true);
    try {
      await Promise.all(files.map(uploadOneFile));
      await fetchAttachments();
    } catch (error) {
      addNotification({
        message: 'Some files failed to upload',
        level: notificationStates.error,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachment) => {
    try {
      await firebase.storage().ref().child(attachment.storagePath).delete();
      await attachmentsRef().doc(attachment.id).delete();
      await fetchAttachments();
    } catch (error) {
      addNotification({
        message: 'Could not delete file',
        level: notificationStates.error,
      });
    }
  };

  return (
    <div className={styles.attachmentsSection}>
      <div className={styles.attachmentsHeader}>
        <span className={styles.attachmentsIconWrap}>
          <FiFile size={16} />
        </span>
        <div className={styles.attachmentsHeaderText}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Semibold}
          >
            Attachments
          </Text>
          <Text variant={textVariantsEnum.Text_xs} color="gray-500">
            {taskId
              ? 'Add files to provide more context to this task.'
              : 'Save the task first to add attachments.'}
          </Text>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleFilesSelected}
        />
        <Button
          text={uploading ? 'Uploading…' : 'Attach Files'}
          icon={<FiUpload size={14} />}
          variant={buttonVariants.outline}
          disabled={!taskId || uploading}
          loading={uploading}
          onClick={() => fileInputRef.current?.click()}
        />
      </div>

      {attachments.length > 0 && (
        <div className={styles.attachmentsList}>
          {attachments.map((file) => (
            <div key={file.id} className={styles.attachmentRow}>
              <FiFile size={14} className={styles.attachmentRowIcon} />
              <div className={styles.attachmentRowText}>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Medium}
                >
                  {file.name}
                </Text>
                <Text variant={textVariantsEnum.Text_xs} color="gray-500">
                  {formatFileSize(file.size)}
                </Text>
              </div>
              <a
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className={styles.attachmentAction}
                aria-label={`Download ${file.name}`}
              >
                <FiDownload size={14} />
              </a>
              {file.uploadedBy === currentUser?.id && (
                <button
                  type="button"
                  className={styles.attachmentAction}
                  onClick={() => handleDelete(file)}
                  aria-label={`Delete ${file.name}`}
                >
                  <FiTrash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

AttachmentsSection.displayName = 'AttachmentsSection';
